import * as ex from 'excalibur';
import { TileType, Allegiance } from '../types/enums';
import { GridPosition } from '../types/interfaces';
import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT } from '../config/constants';
import { Actor } from './actor';
// Import the Game type but define it as any to avoid circular dependencies
type Game = any;

export class Grid {
  public game: Game;
  private tiles: TileType[][];
  private actors: Actor[][];
  private walls: ex.Actor[] = [];

  constructor(game: Game) {
    this.game = game;
    this.tiles = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(TileType.Floor));
    this.actors = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(null));
  }

  // Initialize the grid with walls and actors
  public initialize(pattern: TileType[][]) {
    this.tiles = pattern;
    this.createWalls();
  }

  // Create visual walls based on the tile pattern
  private createWalls() {
    // Clear existing walls first
    for (const wall of this.walls) {
      this.game.remove(wall);
    }
    this.walls = [];

    // Create new walls based on the current pattern
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        if (this.tiles[y][x] === TileType.Wall) {
          const wall = new ex.Actor({
            pos: new ex.Vector(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2),
            width: TILE_SIZE,
            height: TILE_SIZE,
            color: ex.Color.Gray
          });
          this.game.add(wall);
          this.walls.push(wall);
        }
      }
    }
  }

  // Check if a move is valid (within bounds and not a wall or occupied)
  public isValidMove(x: number, y: number): boolean {
    // Check bounds
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) {
      return false;
    }

    // Check if the tile is a wall
    if (this.tiles[y][x] === TileType.Wall) {
      return false;
    }

    // Check if the tile is already occupied
    if (this.actors[y][x] !== null) {
      return false;
    }

    return true;
  }

  // Update actor position in the grid
  public moveActor(actor: Actor, fromX: number, fromY: number, toX: number, toY: number) {
    // Clear old position
    if (fromX >= 0 && fromY >= 0 && fromX < GRID_WIDTH && fromY < GRID_HEIGHT) {
      this.actors[fromY][fromX] = null;
    }

    // Set new position
    if (toX >= 0 && toY >= 0 && toX < GRID_WIDTH && toY < GRID_HEIGHT) {
      this.actors[toY][toX] = actor;
    }
  }

  // Add actor to the grid
  public addActor(actor: Actor, x: number, y: number) {
    if (x >= 0 && y >= 0 && x < GRID_WIDTH && y < GRID_HEIGHT) {
      this.actors[y][x] = actor;
    }
  }

  // Remove actor from the grid
  public removeActor(actor: Actor) {
    const { gridX, gridY } = actor;
    if (gridX >= 0 && gridY >= 0 && gridX < GRID_WIDTH && gridY < GRID_HEIGHT) {
      this.actors[gridY][gridX] = null;
    }
  }

  // Get actor at specific position
  public getActorAt(x: number, y: number): Actor | null {
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) {
      return null;
    }
    return this.actors[y][x];
  }

  // Get all actors within a certain range (Manhattan distance)
  public getActorsInRange(centerX: number, centerY: number, range: number): Actor[] {
    const actors: Actor[] = [];

    for (let y = Math.max(0, centerY - range); y <= Math.min(GRID_HEIGHT - 1, centerY + range); y++) {
      for (let x = Math.max(0, centerX - range); x <= Math.min(GRID_WIDTH - 1, centerX + range); x++) {
        const distance = Math.abs(x - centerX) + Math.abs(y - centerY);
        if (distance <= range && this.actors[y][x] !== null) {
          actors.push(this.actors[y][x]!);
        }
      }
    }

    return actors;
  }

  // Get all actors of a specific allegiance
  public getActorsByAllegiance(allegiance: Allegiance): Actor[] {
    const actors: Actor[] = [];

    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const actor = this.actors[y][x];
        if (actor !== null && actor.allegiance === allegiance) {
          actors.push(actor);
        }
      }
    }

    return actors;
  }

  // Get all cells within a certain range (used for decision-making visualization)
  public getCellsInRange(centerX: number, centerY: number, range: number): GridPosition[] {
    const cells: GridPosition[] = [];

    for (let y = Math.max(0, centerY - range); y <= Math.min(GRID_HEIGHT - 1, centerY + range); y++) {
      for (let x = Math.max(0, centerX - range); x <= Math.min(GRID_WIDTH - 1, centerX + range); x++) {
        const distance = Math.abs(x - centerX) + Math.abs(y - centerY);
        if (distance <= range) {
          cells.push({ x, y });
        }
      }
    }

    return cells;
  }

  // Generate a random pattern for the grid
  public generateRandomPattern(wallPercentage: number = 0.2): TileType[][] {
    const pattern: TileType[][] = Array(GRID_HEIGHT)
      .fill(0)
      .map(() => Array(GRID_WIDTH).fill(TileType.Floor));

    // Add border walls
    for (let x = 0; x < GRID_WIDTH; x++) {
      pattern[0][x] = TileType.Wall;
      pattern[GRID_HEIGHT - 1][x] = TileType.Wall;
    }
    for (let y = 0; y < GRID_HEIGHT; y++) {
      pattern[y][0] = TileType.Wall;
      pattern[y][GRID_WIDTH - 1] = TileType.Wall;
    }

    // Randomly distribute walls
    for (let y = 1; y < GRID_HEIGHT - 1; y++) {
      for (let x = 1; x < GRID_WIDTH - 1; x++) {
        if (Math.random() < wallPercentage) {
          pattern[y][x] = TileType.Wall;
        }
      }
    }

    return pattern;
  }

  // Clear all actors from the grid
  public clearActors() {
    this.actors = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(null));
  }

  // Draw grid lines for debugging
  public drawDebugGrid() {
    // Draw horizontal grid lines
    for (let y = 0; y <= GRID_HEIGHT; y++) {
      const line = new ex.Actor({
        pos: new ex.Vector(GRID_WIDTH * TILE_SIZE / 2, y * TILE_SIZE),
        width: GRID_WIDTH * TILE_SIZE,
        height: 1,
        color: ex.Color.fromHex('#cccccc')
      });
      this.game.add(line);
    }

    // Draw vertical grid lines
    for (let x = 0; x <= GRID_WIDTH; x++) {
      const line = new ex.Actor({
        pos: new ex.Vector(x * TILE_SIZE, GRID_HEIGHT * TILE_SIZE / 2),
        width: 1,
        height: GRID_HEIGHT * TILE_SIZE,
        color: ex.Color.fromHex('#cccccc')
      });
      this.game.add(line);
    }
  }

  // Get all actors in initiative order
  public getActorsInInitiativeOrder(): Actor[] {
    // Flatten the 2D array and filter out null values
    const allActors: Actor[] = [];
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        if (this.actors[y][x] !== null) {
          allActors.push(this.actors[y][x]!);
        }
      }
    }
    // Sort by initiative (higher goes first)
    return allActors.sort((a, b) => b.initiative - a.initiative);
  }
}